import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Radio, RefreshCw, Loader2, CheckCircle2, AlertCircle,
  ChevronDown, ChevronUp, MessageSquare, Edit3, XCircle, MinusCircle, Eye, BookOpen,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatRelative = (iso) => {
  if (!iso) return 'never';
  try {
    const t = new Date(iso);
    const diff = (Date.now() - t.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return t.toLocaleDateString();
  } catch { return iso; }
};

const STATUS_STYLES = {
  parsed: { color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', icon: CheckCircle2, label: 'Parsed' },
  updated: { color: 'bg-blue-500/15 text-blue-300 border-blue-500/30', icon: Edit3, label: 'Updated' },
  error: { color: 'bg-red-500/15 text-red-300 border-red-500/30', icon: XCircle, label: 'Error' },
  save_failed: { color: 'bg-red-500/15 text-red-300 border-red-500/30', icon: XCircle, label: 'Save Failed' },
  not_template: { color: 'bg-gray-500/10 text-gray-400 border-gray-500/20', icon: MinusCircle, label: 'Skipped' },
  unmapped_chat: { color: 'bg-gray-500/10 text-gray-400 border-gray-500/20', icon: MinusCircle, label: 'No Mapping' },
};

export default function TelegramSyncStatusCard({ ibId }) {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncChannelType, setSyncChannelType] = useState('');
  const [syncLimit, setSyncLimit] = useState(50);
  // Optional since-date filter for deep historical scans. When set, backend
  // allows up to 500-message scan and stops when it crosses the cutoff.
  const [syncSinceDate, setSyncSinceDate] = useState('');
  // Diagnostic modal — shown when a sync captures 0 trades so the user can see
  // WHAT the parser was reading and WHY it rejected each message.
  const [syncDiagnostic, setSyncDiagnostic] = useState(null);

  const token = localStorage.getItem('token');
  const h = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const refresh = async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/operations/template/sync-status`, h),
        axios.get(`${API}/operations/template/sync-log?limit=20${ibId ? `&ib_id=${ibId}` : ''}`, h),
      ]);
      setStatus(s.data);
      setLog(l.data?.items || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [ibId]);

  const handleSync = async () => {
    if (!ibId) {
      toast.error('No channel selected');
      return;
    }
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/operations/template/sync-from-telegram`, {
        ib_id: ibId,
        channel_type: syncChannelType || undefined,
        limit: syncLimit,
        since_date: syncSinceDate || undefined,
      }, h);
      const d = res.data;
      if (d.success) {
        toast.success(`Sync complete — Parsed ${d.total_parsed}, Errors ${d.total_errors}, Skipped ${d.total_skipped}`);
        // If nothing was parsed but messages WERE seen, surface the diagnostic
        // modal so admin can inspect exact rejected/skipped message text.
        const anySamples = (d.channels || []).some(c => (c.sample_skipped || []).length > 0);
        if (d.total_parsed === 0 && anySamples) {
          setSyncDiagnostic(d);
        }
      } else {
        toast.error(d.error || 'Sync failed');
      }
      setShowSyncDialog(false);
      await refresh();
    } catch (e) {
      toast.error('Sync failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const ibChannels = status?.chats?.filter((c) => !ibId || c.ib_id === ibId) || [];
  const isActive = status?.enabled && status?.registered;

  return (
    <div className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 border border-cyan-500/20 rounded-xl p-4 mb-4" data-testid="telegram-sync-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`relative w-8 h-8 rounded-full flex items-center justify-center ${isActive ? 'bg-emerald-500/20' : 'bg-gray-500/20'}`}>
            <Radio size={14} className={isActive ? 'text-emerald-400' : 'text-gray-400'} />
            {isActive && <span className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" />}
          </div>
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              Telegram Auto-Sync
              <Badge className={`text-[9px] border ${isActive ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-gray-500/15 text-gray-300 border-gray-500/30'}`} data-testid="sync-status-badge">
                {isActive ? 'LIVE' : status?.enabled === false ? 'DISABLED (preview)' : 'NOT REGISTERED'}
              </Badge>
            </div>
            <div className="text-[10px] text-[hsl(var(--text-muted))]">
              {isActive ? `Started ${formatRelative(status?.started_at)}` : 'Listener inactive — use Sync Now for on-demand pull'}
              {status?.last_event_at && ` · Last event ${formatRelative(status.last_event_at)}`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/telegram-template-guide" target="_blank" rel="noreferrer" data-testid="open-template-guide" title="Template format guide">
            <Button size="sm" variant="outline" className="border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:bg-amber-500/10 hover:text-amber-300 h-8 w-8 p-0">
              <BookOpen size={12} />
            </Button>
          </a>
          <Button size="sm" variant="outline" onClick={refresh} className="border-[hsl(var(--border))] h-8 w-8 p-0" data-testid="sync-refresh-btn" title="Refresh status">
            <RefreshCw size={12} />
          </Button>
          {/* Visible ONLY when MTProto is enabled but the listener is not registered —
              one-click reattach handlers on the live mtproto_client (no backend restart). */}
          {!isActive && status?.enabled !== false && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  const r = await axios.post(`${API}/operations/template/listener/register`, {}, h);
                  if (r.data?.success) {
                    toast.success('Listener re-registered — auto-capture aktif balik');
                    await refresh();
                  } else {
                    toast.error(r.data?.result?.reason || 'Failed to register listener');
                  }
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Failed to register listener');
                }
              }}
              className="border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
              data-testid="listener-register-btn"
              title="Force re-attach Pyrogram on_message handlers on the current MTProto client"
            >
              <Radio size={12} className="mr-1.5" /> Register Listener
            </Button>
          )}
        </div>
      </div>

      {/* Channel breakdown */}
      {ibChannels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {ibChannels.map((c) => (
            <Badge key={c.chat_id} className="text-[9px] bg-[hsl(var(--surface))] text-[hsl(var(--text-secondary))] border border-[hsl(var(--border))] py-0.5 px-1.5" title={`Chat ID: ${c.chat_id}`} data-testid={`sync-chat-${c.chat_id}`}>
              <MessageSquare size={9} className="mr-1" />
              {c.name} {c.channel_type && <span className="ml-1 uppercase text-[8px] opacity-60">{c.channel_type}</span>}
            </Badge>
          ))}
        </div>
      )}
      {ibChannels.length === 0 && ibId && (
        <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded p-2 mb-3 flex items-start gap-2">
          <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
          <span>No Telegram channels linked to this IB. Configure under <strong>Admin → Telegram</strong> to enable auto-sync.</span>
        </div>
      )}

      {/* Log toggle */}
      <button
        onClick={() => setShowLog(!showLog)}
        className="text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1 font-semibold"
        data-testid="sync-log-toggle"
      >
        {showLog ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        Recent Activity ({log.length})
      </button>

      {showLog && (
        <div className="mt-2 space-y-1 max-h-[240px] overflow-y-auto" data-testid="sync-log-list">
          {log.length === 0 ? (
            <div className="text-[11px] text-[hsl(var(--text-muted))] italic text-center py-3">No activity yet.</div>
          ) : (
            log.map((entry) => {
              const cfg = STATUS_STYLES[entry.status] || STATUS_STYLES.not_template;
              const Icon = cfg.icon;
              return (
                <div key={`${entry.telegram_chat_id}-${entry.telegram_message_id}`} className={`border rounded p-2 ${cfg.color}`} data-testid={`sync-log-item-${entry.telegram_message_id}`}>
                  <div className="flex items-center justify-between gap-2 text-[11px] mb-0.5">
                    <span className="flex items-center gap-1.5 font-semibold">
                      <Icon size={11} /> {cfg.label}
                      {entry.is_edit && <span className="text-[9px] opacity-70">(edit)</span>}
                    </span>
                    <span className="text-[10px] opacity-70">{formatRelative(entry.processed_at)}</span>
                  </div>
                  <div className="text-[10px] opacity-90 truncate">
                    {entry.trades_added ? `+${entry.trades_added} trades` : ''}
                    {entry.error ? ` · ${entry.error}` : ''}
                  </div>
                  {entry.raw_text_excerpt && (
                    <div className="text-[10px] opacity-70 mt-1 line-clamp-2 font-mono">
                      {entry.raw_text_excerpt}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Manual sync dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="max-w-md bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="sync-now-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radio size={16} className="text-cyan-400" /> Sync Now from Telegram
            </DialogTitle>
            <DialogDescription className="text-xs">
              Pull last N messages from the linked Telegram channel(s) and process any DONE TODAY posts found.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[hsl(var(--text-muted))]">
              This is a manual fallback in case the live listener was offline. Default scans last <strong>{syncLimit}</strong> messages.
            </p>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Channel Type</label>
              <Select value={syncChannelType || 'both'} onValueChange={(v) => setSyncChannelType(v === 'both' ? '' : v)}>
                <SelectTrigger className="h-9 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] mt-1" data-testid="sync-channel-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <SelectItem value="both">Both (VIP + Public)</SelectItem>
                  <SelectItem value="public">Public only</SelectItem>
                  <SelectItem value="vip">VIP only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))]">Messages to scan</label>
              <Select value={String(syncLimit)} onValueChange={(v) => setSyncLimit(Number(v))}>
                <SelectTrigger className="h-9 text-xs bg-[hsl(var(--surface))] border-[hsl(var(--border))] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                  <SelectItem value="10">Last 10</SelectItem>
                  <SelectItem value="30">Last 30</SelectItem>
                  <SelectItem value="50">Last 50</SelectItem>
                  <SelectItem value="100">Last 100</SelectItem>
                  {syncSinceDate && <SelectItem value="200">Last 200 (deep)</SelectItem>}
                  {syncSinceDate && <SelectItem value="500">Last 500 (deepest)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Optional since-date filter — enables deep scan for missed posts */}
          <div className="pt-3 border-t border-[hsl(var(--border))]">
            <label className="text-[10px] uppercase tracking-wide text-[hsl(var(--text-muted))] block mb-1">
              Since date (optional) — scan all messages from this date onwards
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={syncSinceDate}
                onChange={(e) => setSyncSinceDate(e.target.value)}
                data-testid="sync-since-date"
                className="h-9 text-xs bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded px-2 text-white"
              />
              {syncSinceDate && (
                <Button size="sm" variant="ghost" onClick={() => setSyncSinceDate('')} className="text-[10px] h-7">
                  Clear
                </Button>
              )}
            </div>
            <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1">
              Guna kalau daily performance dah lama diposting dan channel busy dengan promo. Set date sebelum posting tu dan increase limit ke 200-500 untuk scan deep.
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-[hsl(var(--border))]">
            <Button variant="outline" onClick={() => setShowSyncDialog(false)} className="border-[hsl(var(--border))]">Cancel</Button>
            <Button onClick={handleSync} disabled={syncing} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="sync-confirm-btn">
              {syncing ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Eye size={12} className="mr-1.5" />}
              Start Sync
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sync Diagnostic Modal — appears when 0 trades captured. Shows raw
          excerpts of messages the parser saw + why it rejected them. Helps
          admin diagnose channel-linking issues vs template mismatches. */}
      <Dialog open={!!syncDiagnostic} onOpenChange={(v) => !v && setSyncDiagnostic(null)}>
        <DialogContent className="max-w-2xl bg-[hsl(var(--card))] border-[hsl(var(--border))] max-h-[85vh] overflow-y-auto" data-testid="sync-diagnostic-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <XCircle size={16} /> Sync Diagnostic — Kenapa Tiada Trade Ditangkap
            </DialogTitle>
            <DialogDescription className="text-xs">
              Parser scan {syncDiagnostic?.total_skipped ?? 0} mesej tapi tak jumpa trade yang boleh dibaca. Berikut sampel mesej yang di-reject — semak formatnya.
            </DialogDescription>
          </DialogHeader>

          {/* Top-level explanation of WHY & how to fix */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-[11px] space-y-2 mb-3">
            <p className="font-bold text-blue-300">Konteks Cepat:</p>
            <ol className="list-decimal list-inside space-y-1 text-[hsl(var(--text-muted))]">
              <li><strong className="text-white">Auto-capture</strong> berlaku LIVE — bila listener registered & MTProto Connected, setiap post baru dalam channel yang di-link akan diparse dalam masa &lt;5 saat.</li>
              <li><strong className="text-white">Sync Now</strong> ni untuk manual recovery — scan {syncDiagnostic?.channels?.[0]?.parsed !== undefined ? '50' : 'N'} mesej terkini dan proses balik. Guna kalau listener offline masa mesej diposting.</li>
              <li>Kalau semua channel di bawah tunjuk <strong className="text-white">0 parsed / X skipped</strong> — maksudnya mesej yang di-scan bukan format trade summary (contoh: gambar, promo, live update). Parser cari line pattern <code className="bg-black/40 px-1 rounded">Pair + Buy/Sell + Xpips</code>.</li>
              <li>Kalau channel tuan post daily performance TAK ADA dalam list bawah — channel tu belum registered dalam <code>telegram_channel_configs</code>. Contact admin untuk tambah.</li>
            </ol>
          </div>

          <div className="space-y-3">
            {(syncDiagnostic?.channels || []).map((ch, idx) => (
              <div key={idx} className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-white">{ch.name}</p>
                  <div className="flex gap-2 text-[10px]">
                    <span className="text-emerald-400">✓ {ch.parsed} parsed</span>
                    <span className="text-red-400">✗ {ch.errors} errors</span>
                    <span className="text-gray-400">— {ch.skipped} skipped</span>
                  </div>
                </div>
                {ch.error && (
                  <div className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/20 rounded p-2 mb-2">
                    Channel error: {ch.error}
                  </div>
                )}
                {/* Show what parser SUCCESSFULLY captured (rare + valuable to confirm format works) */}
                {(ch.sample_parsed || []).length > 0 && (
                  <div className="mb-2 space-y-1">
                    <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Sample parsed successfully:</p>
                    {(ch.sample_parsed || []).map((p, pi) => (
                      <div key={pi} className="pl-2 border-l-2 border-emerald-500/40">
                        <div className="text-[10px] font-mono text-emerald-300 mb-0.5">msg #{p.message_id} — {p.trades_added} trade(s) added</div>
                        <pre className="text-[11px] text-emerald-100 bg-emerald-500/5 border border-emerald-500/15 rounded p-1.5 whitespace-pre-wrap break-words font-mono max-h-24 overflow-y-auto">
{p.text_excerpt}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
                {(ch.sample_skipped || []).length === 0 && !ch.error && ch.parsed === 0 && (
                  <p className="text-[11px] text-[hsl(var(--text-muted))] italic">Tiada sampel mesej ditangkap dari channel ini.</p>
                )}
                {(ch.sample_skipped || []).map((s, si) => (
                  <div key={si} className="mt-2 pt-2 border-t border-[hsl(var(--border))] first:mt-0 first:pt-0 first:border-t-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-mono text-cyan-400">msg #{s.message_id}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.status === 'error' || s.status === 'save_failed' ? 'bg-red-500/15 text-red-300' : 'bg-gray-500/15 text-gray-400'}`}>
                        {s.status}
                      </span>
                    </div>
                    {s.error && <p className="text-[11px] text-red-300 mb-1">Reason: {s.error}</p>}
                    <pre className="text-[11px] text-[hsl(var(--text))] bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded p-2 whitespace-pre-wrap break-words font-mono max-h-32 overflow-y-auto">
{s.text_excerpt}
                    </pre>
                  </div>
                ))}
              </div>
            ))}
            <div className="text-[11px] text-[hsl(var(--text-muted))] bg-cyan-500/5 border border-cyan-500/20 rounded p-3">
              <p className="font-bold text-cyan-300 mb-1">Semakan pantas:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Adakah mesej memang ada baris seperti <code className="bg-black/40 px-1 rounded">✅ Gold Buy +50pips (VIP)</code>? Parser perlukan direction (Buy/Sell) + pair (Gold/XAU/dll) + pips di line yang sama.</li>
                <li>Jika mesej ni <em>promo/talk-only</em> (bukan summary trades), status <code>not_template</code> memang betul — parser skip.</li>
                <li>Kalau channel tuan post daily performance tak muncul langsung — mungkin belum di-link. Copy-paste text terus dalam Quick Log sebagai workaround.</li>
                <li>Kalau nampak &quot;sample parsed successfully&quot; di atas — bermakna parser dah bekerja untuk mesej yang betul format. Cuma daily performance tuan belum di-scan (mungkin lebih lama daripada N-mesej limit).</li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end pt-3 border-t border-[hsl(var(--border))]">
            <Button variant="outline" onClick={() => setSyncDiagnostic(null)} className="border-[hsl(var(--border))]">Tutup</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
